<script setup lang="ts">
import { onMounted, ref, onUnmounted, watch } from 'vue';
import { router } from '../../Router';
import { RoutePaths } from '../../Router/types/routePaths';
import { UserRole } from '@common/contracts';
import { useUserStore } from '../../stores/userStore/userStore';
import { useUserTokensStore } from '../../stores/userTokensStore/userTokensStore';

const props = defineProps<{
  showLogo?: boolean;
  showGuestInfo?: boolean;
  showGallery?: boolean;
  showCalculators?: boolean;
  alignRight?: boolean;
}>();

const userTokensStore = useUserTokensStore();
const userStore = useUserStore();
const isLoggedIn = ref(userTokensStore.getLoggedInUserTokens?.accessToken ? true : false);
const userRole = ref(userStore.getLoggedInUserRole);
const isMobileMenuOpen = ref(false);
const activeDropdown = ref<string | null>(null);
const isMobile = ref(window.innerWidth <= 780);
const isNavigating = ref(false);

userTokensStore.$onAction(({ name }) => {
  if (name === 'removeUserTokens') {
    isLoggedIn.value = false;
  } else {
    isLoggedIn.value = true;
  }
});

const baseItems = [
  ...(props.showCalculators
    ? [
        {
          label: 'Dodatki',
          icon: 'pi pi-calculator',
          routerLink: RoutePaths.calculators,
          onClick: (): void => {
            router.push(RoutePaths.calculators);
            return;
          },
        },
      ]
    : []),
  ...(props.showGuestInfo && userStore.isVisibleGuestInfo()
    ? [
        {
          label: 'Goście',
          icon: 'pi pi-users',
          items: [
            {
              label: 'Lista gości',
              icon: 'pi pi-list',
              routerLink: RoutePaths.guests,
              onClick: (): void => {
                router.push(RoutePaths.guests);
              },
            },
            {
              label: 'Planer stołów',
              icon: 'pi pi-table',
              routerLink: RoutePaths.guestsTable,
              onClick: (): void => {
                router.push(RoutePaths.guestsTable);
              },
            },
          ],
        },
      ]
    : []),
  ...(props.showGallery && userStore.isVisibleGallery()
    ? [
        {
          label: 'Zdjęcia',
          icon: 'pi pi-image',
          routerLink: RoutePaths.pictures,
          onClick: (): void => {
            router.push(RoutePaths.pictures);
            return;
          },
        },
      ]
    : []),
  {
    label: 'Koszyk',
    icon: 'pi pi-shopping-cart',
    routerLink: 'https://sklep.wspomnieniazwesela.pl/',
    onClick: (): void => {
      window.open('https://sklep.wspomnieniazwesela.pl/', '_blank');
    },
  },
  {
    label: 'Wyloguj',
    icon: 'pi pi-home',
    routerLink: '/',
    onClick: (): void => onLogout(),
  },
];

const items = ref([...baseItems]);

const updateMenuItems = () => {
  const mobileSettingsItem = {
    label: 'Ustawienia',
    icon: 'pi pi-cog',
    routerLink: RoutePaths.settings,
    onClick: (): void => {
      router.push(RoutePaths.settings);
      return;
    },
  };

  if (isMobile.value) {
    const logoutIndex = baseItems.findIndex((item) => item.label === 'Wyloguj');
    items.value = [...baseItems.slice(0, logoutIndex), mobileSettingsItem, ...baseItems.slice(logoutIndex)];
  } else {
    items.value = baseItems.filter((item) => item.label !== 'Ustawienia');
  }
};

const checkMobileDevice = () => {
  isMobile.value = window.innerWidth <= 780;
  updateMenuItems();
};

onMounted(() => {
  window.addEventListener('resize', checkMobileDevice);
  checkMobileDevice();
  if (isLoggedIn.value && userRole.value === UserRole.admin) {
    addAdminPaths();
    return;
  }

  if (isLoggedIn.value && userRole.value === UserRole.guest) {
    items.value = [
      {
        label: 'Wyloguj',
        icon: 'pi pi-home',
        routerLink: '/',
        onClick: (): void => onLogout(),
      },
    ];
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobileDevice);
});

userStore.$onAction(async ({ name, args }) => {
  if (isLoggedIn.value && name === 'setUser') {
    userRole.value = args[0].role;

    const isUserRoleAdmin = userRole.value === UserRole.admin;

    if (isUserRoleAdmin) {
      addAdminPaths();
      return;
    }

    if (userRole.value === UserRole.userMail || userRole.value === UserRole.userToken) {
      items.value = [...baseItems];
      return;
    }

    const isUserGuest = userRole.value === UserRole.guest;

    if (isUserGuest) {
      items.value = [];
      return;
    }
  }
});

const onLogout = (): void => {
  userTokensStore.removeUserTokens();
  userStore.removeUser();
  router.push({
    path: '/',
    replace: true,
  });
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

interface MenuItem {
  label: string;
  icon: string;
  routerLink: string;
  onClick: () => void;
  items?: MenuItem[];
}

const handleMenuItemClick = (item: MenuItem) => {
  const currentPath = router.currentRoute.value.path;
  const targetPath = item.routerLink;

  if (currentPath === targetPath) {
    if (isMobile.value) {
      isMobileMenuOpen.value = false;
    }
    return;
  }

  isNavigating.value = true;
  item.onClick();
};

watch(
  () => router.currentRoute.value,
  () => {
    if (isNavigating.value && isMobile.value) {
      isMobileMenuOpen.value = false;
      isNavigating.value = false;
    }
  },
);

const toggleDropdown = (label: string) => {
  if (activeDropdown.value === label) {
    activeDropdown.value = null;
  } else {
    activeDropdown.value = label;
  }
};

const addAdminPaths = (): void => {
  items.value = [
    {
      label: 'Panel Administratora',
      icon: 'pi pi-folder',
      routerLink: RoutePaths.admin,
      onClick: (): void => {
        router.push(RoutePaths.admin);
        return;
      },
    },
    ...baseItems,
  ];
};
</script>

<template>
  <div
    class="header"
    :class="{
      'header--logged-in': isLoggedIn,
      'header--logged-out': !isLoggedIn,
      'header--align-right': alignRight,
    }"
  >
    <div
      v-if="!isLoggedIn && showLogo"
      class="header__container header__container--center"
    >
      <div class="header__inner">
        <a
          class="header__logo-container"
          href="/"
        >
          <img
            src="/logo-white.png"
            class="header__logo header__logo-image"
            alt="logo - Wspomnienia Z Wesela"
          />
        </a>
      </div>
    </div>
    <nav
      v-if="isLoggedIn"
      class="header__menu"
      :class="{ 'header__menu-align-right': alignRight }"
    >
      <router-link
        v-if="showLogo"
        class="header__logo-container header__logo-container--menu"
        :to="RoutePaths.root"
      >
        <img
          src="/logo-white.png"
          class="header__logo header__logo--menu header__logo-image"
          alt="logo - Wspomnienia Z Wesela"
        />
      </router-link>

      <button
        class="header__menu-button-mobile"
        @click="toggleMobileMenu"
      >
        <i
          class="header__menu-button-icon pi"
          :class="[isMobileMenuOpen ? 'pi-times' : 'pi-bars']"
        ></i>
      </button>

      <ul
        class="header__menu-list"
        :class="{ 'header__menu-list-open': isMobileMenuOpen }"
      >
        <li
          v-for="(item, index) in items"
          :key="index"
          class="header__menu-item"
        >
          <div class="header__menu-item-divider">
            <hr />
          </div>
          <div
            v-if="item.items"
            class="header__menu-dropdown"
          >
            <button
              class="header__menu-button"
              @click="toggleDropdown(item.label)"
            >
              <span :class="item.icon"></span>
              {{ item.label }}
              <span
                class="header__menu-dropdown-arrow"
                :class="{ 'header__menu-dropdown-arrow--active': activeDropdown === item.label }"
              ></span>
            </button>
            <ul
              class="header__menu-dropdown-content"
              :class="{ 'header__menu-dropdown-content--active': activeDropdown === item.label }"
            >
              <li
                v-for="(subItem, subIndex) in item.items"
                :key="subIndex"
                class="header__menu-dropdown-item"
              >
                <a
                  class="header__menu-item-link"
                  :router-link="subItem.routerLink"
                  @click="handleMenuItemClick(subItem)"
                >
                  <button class="header__menu-button">
                    <span :class="subItem.icon"></span>
                    {{ subItem.label }}
                  </button>
                </a>
              </li>
            </ul>
          </div>
          <a
            v-else
            class="header__menu-item-link"
            :router-link="item.routerLink"
            @click="handleMenuItemClick(item)"
          >
            <button class="header__menu-button">
              <span :class="item.icon"></span>
              {{ item.label }}
            </button>
          </a>
        </li>
        <li class="header__menu-item header__menu-item--settings">
          <a
            class="header__menu-item-link"
            :router-link="RoutePaths.settings"
            @click="() => router.push(RoutePaths.settings)"
          >
            <button class="header__menu-button header__menu-button--settings">
              <span class="pi pi-cog"></span>
            </button>
          </a>
        </li>
      </ul>
    </nav>
  </div>
  <div class="placeholder"></div>
</template>

<style lang="scss" scoped>
.placeholder {
  display: block;
  width: 100%;
  height: 6rem;
}

.header {
  display: flex;
  width: 100%;
  background: linear-gradient(135deg, $brown-200 0%, $brown-100 100%);
  height: 6rem;
  position: fixed;
  top: 0;
  z-index: 50;
  box-shadow: 0 2px 10px rgba($black-100, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-top: 3px solid transparent;

  &--logged-in {
    justify-content: center;
  }

  &--align-right {
    justify-content: flex-end;
  }

  &--logged-out {
    justify-content: center;
  }

  &__container {
    display: flex;
    width: 100%;
    max-width: $media-desktop;
    padding: 0 1rem;
    margin: 0 auto;

    &--center {
      justify-content: center;
    }
  }

  &__inner {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  &__logo {
    height: 4.5rem;
    width: auto;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }

    &-container {
      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
      padding: 0.75rem;

      &--menu {
        margin-right: auto;
      }
    }

    &--menu {
      height: 4rem;
    }

    &-image {
      object-fit: contain;
    }
  }

  &__menu {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0 1rem;
    position: relative;
    justify-content: center;
    margin-right: 10rem;

    &-button-mobile {
      display: none;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      margin-left: auto;
      z-index: 100;
      position: relative;

      @media (max-width: $media-max-tablet) {
        display: block;
        position: fixed;
        top: 1rem;
        right: 1rem;
      }
    }

    &-button-icon {
      font-size: 2.5rem;
      color: $white;
      transition: transform 0.3s ease;
      display: block;
      width: 2.5rem;
      height: 2.5rem;
      text-align: center;
      line-height: 2.5rem;

      .header__menu-list-open & {
        transform: rotate(180deg);
      }
    }

    &-align-right {
      justify-content: flex-end;
      margin-right: 10rem;
    }

    &-list {
      display: flex;
      align-items: center;
      list-style: none;
      margin: 0;
      padding: 0;
      gap: 1rem;
      justify-content: center;

      @media (max-width: $media-max-tablet) {
        position: fixed;
        top: 0;
        right: 0;
        width: 100%;
        height: 100vh;
        background: linear-gradient(135deg, $brown-200 0%, $brown-100 100%);
        flex-direction: column;
        justify-content: flex-start;
        padding: 6rem 2rem 2rem;
        transform: translateX(100%);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        z-index: 98;
        overflow-y: auto;
        gap: 0;

        &.header__menu-list-open {
          transform: translateX(0);
        }
      }
    }

    &-item {
      position: relative;

      &--settings {
        position: absolute;
        right: -9rem;
        margin-left: auto;
        padding-right: 1rem;

        @media (max-width: $media-max-tablet) {
          display: none;
        }
      }

      &-divider {
        display: none;

        @media (max-width: $media-max-tablet) {
          display: block;
          width: 100%;
          max-width: 200px;
          margin: 0 auto;
          padding: 0.5rem 0;

          hr {
            border: none;
            height: 1px;
            background: linear-gradient(90deg, transparent, $brown-300, transparent);
            margin: 0;
          }
        }
      }

      &-link {
        text-decoration: none;
        color: $white;
        display: flex;
        align-items: center;
        padding: 0.5rem 1rem;
        border-radius: 0.5rem;
        transition: all 0.3s ease;

        &:hover {
          background-color: rgba($white, 0.1);
        }

        @media (max-width: $media-max-tablet) {
          justify-content: center;
          padding: 0;
          font-size: 2.2rem;
        }
      }
    }

    &-button {
      display: flex;
      align-items: center;
      gap: 1rem;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      width: 100%;
      text-align: left;
      color: $white-100;
      font-size: 1.7rem;

      &--settings {
        font-size: 2rem;
        padding: 0.5rem;
        border-radius: 50%;
        transition: all 0.3s ease;

        &:hover {
          background-color: rgba($white, 0.1);
          transform: rotate(45deg);
        }
      }

      &:hover {
        color: $white-200;
      }

      span {
        font-size: 1.5rem;
      }

      @media (max-width: $media-max-tablet) {
        width: 100%;
        justify-content: center;
        padding: 1.5rem;
        font-size: 2.2rem;

        span {
          font-size: 2.2rem;
        }
      }
    }
  }

  &__menu-dropdown {
    position: relative;
    display: inline-block;
    padding: 0.5rem 1rem;

    @media (min-width: $media-desktop) {
      &:hover {
        .header__menu-dropdown-content {
          display: block;
        }
      }
    }

    @media (max-width: $media-max-tablet) {
      width: 100%;
    }
  }

  &__menu-dropdown-arrow {
    display: none;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid $white;
    margin-left: 8px;
    transition: transform 0.3s ease;

    &--active {
      transform: rotate(180deg);
    }

    @media (max-width: $media-max-tablet) {
      display: inline-block;
    }
  }

  &__menu-dropdown-content {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    background: linear-gradient(135deg, $brown-200 0%, $brown-100 100%);
    min-width: 200px;
    box-shadow: 0 2px 10px rgba($black-100, 0.1);
    z-index: 51;
    border-radius: 4px;
    padding: 0.5rem 0;
    list-style: none;

    @media (min-width: $media-desktop) {
      display: none;
    }

    @media (max-width: $media-max-tablet) {
      position: static;
      width: 100%;
      min-width: unset;
      box-shadow: none;
      border-radius: 0;
      background: rgba($brown-200, 0.3);
      margin-top: 0.5rem;
    }

    &--active {
      display: block;
      animation: slideDown 0.3s ease forwards;
    }
  }

  &__menu-dropdown-item {
    padding: 0.5rem 1rem;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba($white-100, 0.1);
    }

    @media (max-width: $media-max-tablet) {
      padding: 1rem;
      text-align: center;
      border-bottom: 1px solid rgba($white-100, 0.1);

      &:last-child {
        border-bottom: none;
      }
    }
  }
}

@keyframes slide-down {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slide-in {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
